import React, { useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, Download, QrCode, Smartphone, Check, Share2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useToast } from '../hooks/use-toast';

export default function ShareBiosite({ slug, name }) {
  const url = `${window.location.origin}/${slug}`;
  const [showQR, setShowQR] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedNfc, setCopiedNfc] = useState(false);
  const canvasRef = useRef(null);
  const { toast } = useToast();

  const doCopy = async (which = 'link') => {
    await navigator.clipboard.writeText(url);
    if (which === 'nfc') { setCopiedNfc(true); setTimeout(() => setCopiedNfc(false), 2200); }
    else { setCopied(true); setTimeout(() => setCopied(false), 2200); }
    toast({ title: 'Link copiado!', description: url });
  };

  const download = () => {
    const container = canvasRef.current;
    if (!container) return;
    const canvas = container.querySelector('canvas');
    if (!canvas) return;
    // Compose a nicer printable PNG: QR on top, title + URL below
    const S = 1024;
    const out = document.createElement('canvas');
    out.width = S; out.height = S + 220;
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, out.width, out.height);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(canvas, 0, 0, S, S);
    ctx.fillStyle = '#0f0f10'; ctx.textAlign = 'center';
    ctx.font = '600 56px -apple-system, Inter, sans-serif';
    ctx.fillText(name || slug, S / 2, S + 90);
    ctx.fillStyle = '#555'; ctx.font = '500 36px -apple-system, Inter, sans-serif';
    ctx.fillText(url.replace(/^https?:\/\//, ''), S / 2, S + 150);
    const data = out.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = data; a.download = `biosite-${slug}.png`; a.click();
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: name || slug, url }); } catch { /* cancel */ }
    } else { doCopy(); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-neutral-200 p-5">
        <div className="flex items-center gap-1.5 text-xs text-neutral-500 mb-2"><ExternalLink size={12} /> URL pública (permanente)</div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[220px] bg-neutral-50 rounded-lg px-3 py-2.5 border border-neutral-200 font-mono text-sm break-all select-all">{url}</div>
          <Button onClick={() => doCopy('link')} className="gap-2 bg-neutral-900 hover:bg-neutral-800 text-white">
            {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar link</>}
          </Button>
          <Button variant="outline" onClick={share} className="gap-2 border-neutral-300">
            <Share2 size={14} /> Compartilhar
          </Button>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="gap-2 border-neutral-300"><ExternalLink size={14} /> Abrir</Button>
          </a>
        </div>
        <div className="mt-3 text-xs text-neutral-500">
          Esta URL é definitiva. Nenhuma edição no painel vai alterar o endereço — ela pode ser impressa, gravada em tag NFC e compartilhada livremente.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold text-neutral-900"><QrCode size={16} /> QR Code</div>
          </div>
          <div ref={canvasRef} className="grid place-items-center bg-neutral-50 rounded-xl p-4 border border-neutral-200">
            <QRCodeCanvas value={url} size={192} level="M" includeMargin />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowQR(true)} className="gap-2 border-neutral-300"><QrCode size={14} /> Visualizar grande</Button>
            <Button onClick={download} className="gap-2 bg-neutral-900 hover:bg-neutral-800 text-white"><Download size={14} /> Baixar PNG</Button>
          </div>
          <div className="mt-2 text-xs text-neutral-500">Imprima e cole na sua loja. O QR Code aponta para a URL pública — nunca para o painel.</div>
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200 p-5">
          <div className="flex items-center gap-2 font-semibold text-neutral-900"><Smartphone size={16} /> Link para NFC</div>
          <p className="text-xs text-neutral-500 mt-1">Grave esta URL em uma tag NFC usando um app como NFC Tools. Encostando o celular na tag, o Biosite abre direto.</p>
          <div className="mt-3 bg-neutral-50 rounded-lg px-3 py-2.5 border border-neutral-200 font-mono text-sm break-all">{url}</div>
          <Button onClick={() => doCopy('nfc')} variant="outline" className="mt-3 w-full gap-2 border-neutral-300">
            {copiedNfc ? <><Check size={14} /> Copiado para NFC</> : <><Copy size={14} /> Copiar link para NFC</>}
          </Button>
          <div className="mt-3 text-xs text-neutral-500">
            Dica: a tag funciona com Android e com iPhone compatível (iPhone XS ou mais novo).
          </div>
        </div>
      </div>

      <Dialog open={showQR} onOpenChange={setShowQR}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-display">QR Code do biosite</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="bg-white p-4 rounded-xl border border-neutral-200">
              <QRCodeCanvas value={url} size={280} level="M" includeMargin />
            </div>
            <div className="text-sm font-mono text-neutral-700 break-all text-center">{url}</div>
            <Button onClick={download} className="w-full gap-2 bg-neutral-900 hover:bg-neutral-800 text-white"><Download size={14} /> Baixar PNG</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
